import { NavLink, useNavigate } from 'react-router-dom';
import { Layout, ShoppingCart, Package, Users, Mail, DollarSign, Clock, FileText, LogOut, Tv, X, ChevronLeft, ChevronRight, UserCheck } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext.jsx';

const API_URL = import.meta.env.VITE_API_URL;

const ALL_NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: Layout, roles: ['admin'] },
  { to: '/inventory', label: 'Inventory', icon: Package, roles: ['admin'] },
  { to: '/pos', label: 'POS', icon: ShoppingCart, roles: ['admin'] },
  { to: '/accounting', label: 'Accounting', icon: DollarSign, roles: ['admin', 'accountant'] },
  { to: '/attendance', label: 'Attendance', icon: Clock, roles: ['admin'] },
  { to: '/payroll', label: 'Payroll', icon: DollarSign, roles: ['admin', 'accountant'] },
  { to: '/reports', label: 'Reports & Analytics', icon: FileText, roles: ['admin'] },
  { to: '/customers', label: 'Customers', icon: Users, roles: ['admin'] },
  { to: '/comms-marketing', label: 'Comms & Marketing', icon: Mail, roles: ['admin'] },
  { to: '/signage', label: 'Signage', icon: Tv, roles: ['admin'] },
  { to: '/recruitment', label: 'Recruitment', icon: UserCheck, roles: ['admin'] },
];

function Sidebar({ isOpen, onClose, collapsed = false, onToggleCollapse }) {
  const { logout, user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [needsReviewCount, setNeedsReviewCount] = useState(0);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const poll = () => {
      axios.get(`${API_URL}/api/recruitment/needs-review`)
        .then((res) => setNeedsReviewCount(res.data.count))
        .catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 60000);
    return () => clearInterval(interval);
  }, [user]);

  if (loading || !user) return null;

  const navItems = ALL_NAV_ITEMS.filter(item => item.roles.includes(user.role));

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={onClose}
        />
      )}

      <div
        className={`
          ${collapsed ? 'md:w-16' : 'md:w-64'} w-64
          min-h-screen flex flex-col fixed top-0 left-0 bg-gray-800 text-white font-libre z-30
          transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
      >
        {/* Header */}
        <div className="py-4 relative flex items-center justify-center">
          <img src="/ellas.jpeg" alt="ELLAS Logo" className={`h-12 ${collapsed ? 'md:hidden' : ''}`} />
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 md:hidden"
          >
            <X size={18} />
          </button>
          {/* Desktop collapse toggle */}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="hidden md:flex absolute top-3 right-3 p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="space-y-1 px-2 flex-1">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-3 py-2 px-3 rounded transition duration-200 text-sm font-medium ${
                    collapsed ? 'md:justify-center' : ''
                  } ${
                    isActive ? 'bg-blue-500 hover:bg-blue-600' : 'hover:bg-blue-600'
                  }`
                }
              >
                <IconComponent size={20} className="flex-shrink-0" />
                <span className={`flex-1 flex items-center justify-between ${collapsed ? 'md:hidden' : ''}`}>
                  {item.label}
                  {item.to === '/recruitment' && needsReviewCount > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {needsReviewCount}
                    </span>
                  )}
                </span>
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-2">
          <div className={`text-xs text-gray-400 px-3 py-2 border-t border-gray-700 truncate ${collapsed ? 'md:hidden' : ''}`}>
            Logged in as: {user.username}
          </div>
          <button
            onClick={handleLogout}
            title={collapsed ? 'Logout' : undefined}
            className={`w-full flex items-center py-2 px-3 rounded transition duration-200 bg-red-600 hover:bg-red-700 text-white ${
              collapsed ? 'md:justify-center' : 'justify-start'
            }`}
          >
            <LogOut className={`w-5 h-5 flex-shrink-0 ${collapsed ? 'md:mr-0' : 'mr-2'}`} />
            <span className={collapsed ? 'md:hidden' : ''}>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
}

export default Sidebar;
